import { useCallback, useEffect, useReducer } from "react";
import type { PreloadedSimDeviceVerifyFieldKey } from "../utils/preloaded-sim-device-verify-attempt-storage";
import {
  getPreloadedSimDeviceVerifyAttemptStorageKey,
  getPreloadedSimDeviceVerifyFieldSnapshot,
  recordPreloadedSimDeviceVerifyFieldAttempt,
  remainingPreloadedSimDeviceVerifyCooldownSeconds,
} from "../utils/preloaded-sim-device-verify-attempt-storage";

export type UsePreloadedSimDeviceVerifyFieldRateLimitResult = {
  inCooldown: boolean;
  isBlocked: boolean;
  remainingSeconds: number;
  /** After each verify request for this field completes (success or error). */
  registerAttempt: () => void;
};

/**
 * Per-field rate limit (SIM vs IMEI) when verify buttons are shown.
 * Same idea as {@link useImeiCheckRateLimit}: every attempt counts; 3 attempts → 120s cooldown; one localStorage blob with `sim` + `imei` keys.
 */
export function usePreloadedSimDeviceVerifyFieldRateLimit(
  enabled: boolean,
  field: PreloadedSimDeviceVerifyFieldKey,
): UsePreloadedSimDeviceVerifyFieldRateLimitResult {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const storageKey = getPreloadedSimDeviceVerifyAttemptStorageKey();

  const snapshot = enabled
    ? getPreloadedSimDeviceVerifyFieldSnapshot(field)
    : {
        attempts: 0,
        inCooldown: false,
        cooldownUntil: null,
        attemptsRemaining: 3,
      };

  const remainingSeconds = remainingPreloadedSimDeviceVerifyCooldownSeconds(
    snapshot.cooldownUntil,
  );
  const inCooldown = Boolean(enabled && snapshot.inCooldown);
  const isBlocked = inCooldown;

  useEffect(() => {
    if (!enabled) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey) bump();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [enabled, storageKey]);

  useEffect(() => {
    if (!enabled || !inCooldown) return;
    const id = window.setInterval(() => bump(), 1000);
    return () => window.clearInterval(id);
  }, [enabled, inCooldown]);

  const registerAttempt = useCallback(() => {
    if (!enabled) return;
    recordPreloadedSimDeviceVerifyFieldAttempt(field);
    bump();
  }, [enabled, field]);

  return {
    inCooldown,
    isBlocked,
    remainingSeconds,
    registerAttempt,
  };
}
